import React, { FC } from 'react';
import Box from '@mui/material/Box';
import { CalendarIcon, ClearIcon, DesktopDatePicker } from '@mui/x-date-pickers';
import { SxProps, styled } from '@mui/material/styles';

import './TwDatePicker.scss';
import { InfoIcon } from '../TwInfoCard/TwInfoCard.styles';
import TwTooltip from '../TwTooltip/TwTooltip';

interface TwDatePickerProps {
  /** Id for the text field box. */
  id?: string;
  /** Name for the text field box. */
  name?: string;
  /** Text that goes over the text field box. */
  label?: string;
  /** Tooltip text for the label. */
  infoTooltipLabel?: string;
  /** Placeholder of the text field box. */
  placeholder?: string;
  /** Classname for the text field. */
  textFieldClassName?: string;
  /** Classname for the container. */
  containerClassName?: string;
  /** Sx element for the container. */
  containerSx?: SxProps;
  /** Value of the datepicker. */
  value: unknown;
  /** Datepicker change event. */
  onChange: (date: unknown, keyboardInputValue?: string | undefined) => void;
  /** Datepicker input change event. */
  onInputChange?: (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
  /** Datepicker input blur event. */
  onInputBlur?: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>) => void;
  /** Error flag. */
  error?: boolean;
  /* Highlight the input border */
  highlight?: boolean;
  /* format of the date when rendered in the input(s) */
  format?: string;
  /* flag to open/close date picker */
  open?: boolean;
  /* open event handler */
  disabled?: boolean;
  /* readOnlu*/
  readOnly?: boolean;
  onOpen?: () => void;
  /* close event handler */
  onClose?: () => void;
  /* min date */
  minDate?: Date;
  /* max date */
  maxDate?: Date;
}

const StyledDatepickerLabel = styled('label')({
  color: '#7e858e',
  display: 'inline-block',
  fontSize: '14px',
  padding: '4px 0px',
  fontWeight: 500
});

const ClearDatePickerIcon = styled(ClearIcon)({
  width: '18px',
  height: '18px'
});

const OpenDatePickerIcon = styled(CalendarIcon)({
  width: '18px',
  height: '18px'
});

const TwDatePicker: FC<TwDatePickerProps> = ({
  id,
  name,
  label,
  infoTooltipLabel,
  placeholder,
  textFieldClassName,
  containerClassName,
  containerSx,
  value,
  onChange,
  onInputChange,
  onInputBlur,
  error,
  highlight = false,
  format,
  open,
  disabled,
  readOnly = false,
  onOpen,
  onClose,
  minDate,
  maxDate
}) => {
  return (
    <Box className={containerClassName} sx={containerSx}>
      {!!label && (
        <StyledDatepickerLabel>
          <span>{label}</span>
          {infoTooltipLabel && (
            <TwTooltip title={infoTooltipLabel} placement="top">
              <InfoIcon
                sx={{
                  fill: '#D1D5DB',
                  height: '18px',
                  width: '18px',
                  verticalAlign: 'sub',
                  ml: '3px'
                }}
              />
            </TwTooltip>
          )}
        </StyledDatepickerLabel>
      )}
      <DesktopDatePicker
        minDate={minDate}
        maxDate={maxDate}
        open={open}
        value={value}
        onOpen={(): void => onOpen && onOpen()}
        onClose={(): void => onClose && onClose()}
        onChange={(newDate: any): void => onChange(newDate)}
        slots={{
          clearIcon: ClearDatePickerIcon,
          openPickerIcon: OpenDatePickerIcon
        }}
        readOnly={readOnly}
        disabled={disabled}
        format={format}
        slotProps={{
          openPickerButton: { size: 'small' },
          clearButton: { size: 'small' },
          field: { clearable: true },
          textField: {
            sx: {
              border: highlight ? '1px solid #FCA5A5' : undefined,
              borderRadius: '4px',

              '& .Mui-disabled fieldset': {
                borderColor: '#E2E2EA !important'
              }
            },
            className: `tw-desktop-date-picker ${textFieldClassName || ''}`,
            id: id,
            error: error,
            placeholder: placeholder,
            name: name,
            onChange: (e): void => {
              onInputChange && onInputChange(e);
            },
            onBlur: (e): void => {
              onInputBlur && onInputBlur(e);
            }
          }
        }}
      />
    </Box>
  );
};

export default TwDatePicker;
